import React, { useMemo, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { getLocaleContent } from '@helper/locale-content.js';
import LinkNewsDetail from '@components/common/Link/LinkNewsDetail';
import NewsBookingForm from './NewsBookingForm';
import { useNewsStore } from '@stores/useNewsStore';
import NewsService from '@services/news.service';
import {safeHtml} from '@helper/sanitize-html';

const NewsDetails = props => {
    const { locale, pathname } = useRouter();
    const { preloadedNews } = props;
    const newsDetail = preloadedNews || {};
    const newsFromStore = useNewsStore(state => state.news);
    const setNewsStore = useNewsStore(state => state.setNews);
    const isPromotionPage = newsDetail.is_promotion === 1 || newsDetail.is_promotion === '1';

    // Fetch tin tức cùng danh mục khi component mount
    const fetchNewsByCategory = useCallback(() => {
        // Chỉ fetch nếu không có related_news từ API và có news_category_id
        if (!newsDetail.related_news || newsDetail.related_news.length === 0) {
            if (newsDetail.news_category_id && locale) {
                const newsService = new NewsService();
                newsService
                    .getNewsList({
                        locale,
                        pageSize: 20,
                        pageIndex: 1,
                        news_category_id: newsDetail.news_category_id,
                        is_news: 1
                    })
                    .then(res => {
                        const items = res?.data || [];
                        const total = parseInt(res?.total) || 0;
                        setNewsStore(items, total, 1, 20);
                    })
                    .catch(err => {
                        console.error('Error fetching related news:', err);
                    });
            }
        }
    }, [newsDetail.related_news, newsDetail.news_category_id, locale, setNewsStore]);

    // Effect để fetch news khi component mount
    useEffect(() => {
        fetchNewsByCategory();
    }, [fetchNewsByCategory]);

    // Xử lý tin tức liên quan
    const relatedNews = useMemo(() => {
        // Nếu có related_news từ API, sử dụng luôn
        const apiRelatedNews = newsDetail.related_news || [];
        if (apiRelatedNews.length > 0) {
            return apiRelatedNews;
        }

        // Nếu không có, lấy 4 tin mới nhất cùng danh mục từ store
        if (!newsDetail.news_category_id || !newsFromStore || newsFromStore.length === 0) {
            return [];
        }

        // Lọc tin cùng danh mục, loại bỏ tin hiện tại, sắp xếp theo ngày, lấy 4 tin
        return newsFromStore
            .filter(news =>
                news.news_category_id === newsDetail.news_category_id &&
                news.news_id !== newsDetail.news_id
            )
            .sort((a, b) => {
                const dateA = new Date(a.created_date || a.news_date || 0);
                const dateB = new Date(b.created_date || b.news_date || 0);
                return dateB - dateA;
            })
            .slice(0, 4);
    }, [newsDetail.related_news, newsDetail.news_category_id, newsDetail.news_id, newsFromStore]);

    // Xác định loại trang để hiển thị tiêu đề phù hợp
    const getRelatedNewsTitle = () => {
        if (isPromotionPage) {
            return getLocaleContent(locale, 'page.promotion-page.related-promotions');
        } else if (pathname.includes('bao-chi')) {
            return getLocaleContent(locale, 'page.press-news.related-press');
        }
        return getLocaleContent(locale, 'page.news.related-news');
    };

    // Trích xuất các heading từ nội dung để tạo mục lục
    const extractHeadings = content => {
        if (!content) return [];
        const headingRegex = /<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi;
        const headings = [];
        let match;
        while ((match = headingRegex.exec(content)) !== null) {
            const text = match[1].replace(/<[^>]*>/g, '').trim();
            if (text) {
                const id = text
                    .toLowerCase()
                    .replace(/[^\w\s]/g, '')
                    .replace(/\s+/g, '-');
                headings.push({
                    text: text,
                    id: id,
                    level: match[0].match(/<h([1-6])/)[1],
                });
            }
        }
        return headings;
    };

    // Tạo ID cho heading text
    const createHeadingId = text => {
        return text
            .replace(/<[^>]*>/g, '')
            .toLowerCase()
            .replace(/[^\w\s]/g, '')
            .replace(/\s+/g, '-');
    };

    const articleHtml = safeHtml(
        (
            newsDetail.content ||
            newsDetail.short_description ||
            getLocaleContent(locale, 'page.news.content-updating')
        ).replace(/<h([1-6])([^>]*)>(.*?)<\/h[1-6]>/gi, (match, level, attrs, text) => {
            const id = createHeadingId(text);
            return `<h${level}${attrs} id="${id}">${text}</h${level}>`;
        }),
    );
    const headings = extractHeadings(articleHtml.__html);

    // Xử lý scroll đến section cụ thể
    const handleScroll = id => {
        const section = document.getElementById(id);
        if (section) {
            const headerHeight = 160 + 20; // header 160, 20 padding top
            const top = section.getBoundingClientRect().top + window.pageYOffset - headerHeight;
            window.scrollTo({
                top,
                behavior: 'smooth',
            });
        }
    };
    // Chức năng chia sẻ
    const handleShare = () => {
        if (navigator.share) {
            // Sử dụng Web Share API nếu có
            navigator.share({
                title: newsDetail.news_title,
                text: newsDetail.short_description,
                url: window.location.href,
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
        }
    };

    // Chia sẻ Facebook
    const handleFacebookShare = () => {
        const url = window.location.href;
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        window.open(facebookUrl, '_blank', 'width=600,height=400');
    };

    // Chia sẻ X (Twitter)
    const handleXShare = () => {
        const url = window.location.href;
        const title = newsDetail.news_title || '';
        const text = `${title} - ${newsDetail.short_description || ''}`;
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
    };

    return (
        <div className="news-detail-container">
            <div className="news-detail-content">
                <h1 className="news-detail-title-full">{newsDetail.news_title || 'Tiêu đề tin tức'}</h1>
                <div className="news-details-meta">
                    <div className="news-meta-date-author">
                        <div className={`date ${isPromotionPage ? 'promotion-date' : ''}`}>
                            <img
                                src="/bw_image/ic_appoinment_news.png"
                                alt="Date"
                                style={isPromotionPage ? {
                                    filter: 'brightness(0) saturate(100%) invert(18%) sepia(98%) saturate(3567%) hue-rotate(352deg) brightness(89%) contrast(98%)'
                                } : {}}
                            />
                            <span>
                                {isPromotionPage ? (
                                    newsDetail.promotion_from_date && newsDetail.promotion_to_date
                                        ? <>
                                            <strong>{getLocaleContent(locale, 'page.promotion-page.promotion-period')}</strong> {getLocaleContent(locale, 'page.promotion-page.from-date')} {newsDetail.promotion_from_date} {getLocaleContent(locale, 'page.promotion-page.to-date')} {newsDetail.promotion_to_date}
                                        </>
                                        : <>
                                            <strong>{getLocaleContent(locale, 'page.promotion-page.promotion-period')}</strong> {newsDetail.news_date_view || 'N/A'}
                                        </>
                                ) : (
                                    `${getLocaleContent(locale, 'page.news.posted-date')} ${newsDetail.news_date_view || 'N/A'}`
                                )}
                            </span>
                        </div>
                        {!isPromotionPage && (
                            <div className="author">
                                <img src="/bw_image/ic_position_news.png" alt="Author" />
                                <span>{getLocaleContent(locale, 'page.news.posted-by')} {newsDetail.created_user || 'TNH'}</span>
                            </div>
                        )}
                    </div>
                    <div className="news-meta-share">
                        <button className="share" onClick={handleShare}>
                            <img src="/bw_image/ic-share_news.png" alt="Share" />
                            <span>{getLocaleContent(locale, 'page.news.share')}</span>
                        </button>

                        {/* Nút chia sẻ Facebook */}
                        <div
                            className="facebook"
                            onClick={handleFacebookShare}
                            style={{ cursor: 'pointer', margin: '5px' }}>
                            <img
                                src="/bw_image/i_facebook_share.png"
                                alt="Facebook"
                                style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                                title="Facebook Share"
                            />
                        </div>

                        {/* Nút chia sẻ X (Twitter) */}
                        <div className="x" onClick={handleXShare} style={{ cursor: 'pointer' }}>
                            <img
                                src="/bw_image/i_x_share.png"
                                alt="X"
                                style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Mục lục nội dung nếu có heading */}
                {headings.length > 0 && (
                    <div className="news-quickview">
                        <h2>{getLocaleContent(locale, 'page.news.quick-view-title')}</h2>
                        <ul>
                            {headings.map((heading, index) => (
                                <li key={index}>
                                    <p onClick={() => handleScroll(heading.id)}>{heading.text}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Nội dung chính của bài viết */}
                <div className="news-body-details">
                    {/* Hình ảnh banner nếu có */}
                    {newsDetail.banner_url && (
                        <img
                            src={newsDetail.banner_url}
                            alt={newsDetail.news_title}
                            style={{ width: '100%', marginBottom: '20px' }}
                        />
                    )}
                    {/* Nội dung HTML với ID cho các heading */}
                    <div dangerouslySetInnerHTML={articleHtml}></div>
                </div>
            </div>
            {/* Sidebar - Tin tức liên quan */}
            <div className="news-sidebar-bar"></div>
            <aside className="news-sidebar">
                {/* Chỉ hiển thị khối tin tức liên quan nếu có dữ liệu */}
                {relatedNews.length > 0 && (
                    <div className="related-news">
                        <h3>{getRelatedNewsTitle()}</h3>
                        <ul>
                            {relatedNews.map((news, index) => (
                                <LinkNewsDetail key={news.news_id || index} news={news}>
                                    <li
                                        style={{
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-4px)';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 105, 64, 0.15)';
                                            const img = e.currentTarget.querySelector('img');
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                            const img = e.currentTarget.querySelector('img');
                                        }}
                                    >
                                        <img
                                            src={news.image_url || news.banner_url || '/bw_image/news-relate-thumb.png'}
                                            alt={news.news_title}
                                            style={{
                                                transition: 'transform 0.3s ease'
                                            }}
                                        />
                                        <p style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {news.news_title}
                                        </p>
                                    </li>
                                </LinkNewsDetail>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Form đăng ký khám */}
                <div className="register-form">
                    <h3>{getLocaleContent(locale, 'page.booking-form.register-now-title')}</h3>
                    <NewsBookingForm />
                </div>
                {/* <div className="news-details-more">
                    <button>
                        Xem thêm <img src="/bw_image/chevron-right.png"></img>
                    </button>
                </div> */}
            </aside>
        </div>
    );
};

export default NewsDetails;
